import { useEffect, useState, useRef } from 'react';

const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOM_APP_ID;

const MockIntercomWidget = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    { id: 1, from: 'bot', text: 'Hi there! How can we help you today?', time: new Date() }
  ]);
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleSend = () => {
    const text = input.trim();
    if (!text) return;
    setMessages(prev => [...prev, { id: Date.now(), from: 'user', text, time: new Date() }]);
    setInput('');
    setTimeout(() => {
      setMessages(prev => [...prev, {
        id: Date.now() + 1, from: 'bot',
        text: 'Thanks for your message! A support agent will get back to you shortly.',
        time: new Date()
      }]);
    }, 1200);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (d) => {
    const h = d.getHours();
    const m = d.getMinutes().toString().padStart(2, '0');
    const ampm = h >= 12 ? 'PM' : 'AM';
    return `${h % 12 || 12}:${m} ${ampm}`;
  };

  const bubbleStyle = {
    position: 'fixed', bottom: '20px', right: '20px', width: '60px', height: '60px',
    borderRadius: '50%', background: '#4a6741', color: '#fff', border: 'none',
    cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
    boxShadow: '0 4px 16px rgba(0,0,0,0.2)', zIndex: 9999, transition: 'transform 0.2s'
  };

  const panelStyle = {
    position: 'fixed', bottom: '90px', right: '20px', width: '360px', maxHeight: '500px',
    borderRadius: '12px', background: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
    zIndex: 9998, display: 'flex', flexDirection: 'column', overflow: 'hidden',
    border: '0.5px solid #e0d6cc'
  };

  const headerStyle = {
    background: '#4a6741', color: '#fff', padding: '16px 20px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
  };

  const messagesStyle = {
    flex: 1, overflowY: 'auto', padding: '16px', display: 'flex',
    flexDirection: 'column', gap: '10px', maxHeight: '320px', minHeight: '200px'
  };

  const inputAreaStyle = {
    display: 'flex', padding: '12px', borderTop: '0.5px solid #e0d6cc', gap: '8px'
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(!isOpen)}
        style={bubbleStyle}
        onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = 'scale(1)'; }}
        aria-label="Open chat"
      >
        {isOpen ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )}
      </button>

      {isOpen && (
        <div style={panelStyle}>
          <div style={headerStyle}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '16px' }}>Oval Support</div>
              <div style={{ fontSize: '12px', opacity: 0.8 }}>We typically reply within a few hours</div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '4px' }}
              aria-label="Close chat"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div style={messagesStyle}>
            {messages.map((msg) => (
              <div
                key={msg.id}
                style={{
                  alignSelf: msg.from === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '80%'
                }}
              >
                <div style={{
                  padding: '10px 14px',
                  borderRadius: msg.from === 'user' ? '14px 14px 4px 14px' : '14px 14px 14px 4px',
                  background: msg.from === 'user' ? '#4a6741' : '#f5f0eb',
                  color: msg.from === 'user' ? '#fff' : '#333',
                  fontSize: '14px', lineHeight: '1.4'
                }}>
                  {msg.text}
                </div>
                <div style={{
                  fontSize: '11px', color: '#999', marginTop: '4px',
                  textAlign: msg.from === 'user' ? 'right' : 'left'
                }}>
                  {formatTime(msg.time)}
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          <div style={inputAreaStyle}>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type a message..."
              style={{
                flex: 1, padding: '10px 14px', border: '0.5px solid #e0d6cc',
                borderRadius: '8px', fontSize: '14px', outline: 'none',
                fontFamily: 'inherit'
              }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              style={{
                padding: '10px 16px', background: input.trim() ? '#4a6741' : '#ccc',
                color: '#fff', border: 'none', borderRadius: '8px', cursor: input.trim() ? 'pointer' : 'default',
                fontSize: '14px', fontWeight: 500, transition: 'background 0.2s'
              }}
            >
              Send
            </button>
          </div>
        </div>
      )}
    </>
  );
};

const bootIntercom = (appId, user) => {
  const w = window;
  w.intercomSettings = {
    api_base: 'https://api-iam.intercom.io',
    app_id: appId
  };

  // Set up Intercom shim (queue) if not already present
  if (typeof w.Intercom !== 'function') {
    const i = (...args) => { i.c(args); };
    i.q = [];
    i.c = (args) => { i.q.push(args); };
    w.Intercom = i;
  }

  // Load widget script
  const s = document.createElement('script');
  s.type = 'text/javascript';
  s.async = true;
  s.src = `https://widget.intercom.io/widget/${appId}`;
  const x = document.getElementsByTagName('script')[0];
  if (x && x.parentNode) {
    x.parentNode.insertBefore(s, x);
  } else {
    document.head.appendChild(s);
  }

  // Boot with user data
  w.Intercom('boot', { app_id: appId, ...user });

  return s;
};

const IntercomWidget = ({ user = null, isDemo = false }) => {
  useEffect(() => {
    if (isDemo) return;
    if (!INTERCOM_APP_ID) return;

    const script = bootIntercom(INTERCOM_APP_ID, user);

    return () => {
      if (window.Intercom) {
        window.Intercom('shutdown');
      }
      script.remove();
    };
  }, [user, isDemo]);

  if (isDemo) {
    return <MockIntercomWidget />;
  }

  return null;
};

export const showIntercom = () => {
  if (window.Intercom) window.Intercom('show');
};

export const hideIntercom = () => {
  if (window.Intercom) window.Intercom('hide');
};

export const showIntercomNewMessage = (prePopulatedMessage = '') => {
  if (window.Intercom) window.Intercom('showNewMessage', prePopulatedMessage);
};

export const trackIntercomEvent = (eventName, metadata = {}) => {
  if (window.Intercom) window.Intercom('trackEvent', eventName, metadata);
};

export default IntercomWidget;
